import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  RootState,
  selectCwTxDataAvailableTimes,
  selectExtTxDataAvailableTimes,
  selectPositionsDataAvailableTimes,
  selectSessionStartTimes,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import { Subject } from 'rxjs'
import { takeUntil, withLatestFrom } from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class SessionDataAvailabilityService {
  private readonly _destroyed$ = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly telemetryService: TelemetryService,
  ) {}

  initialize() {
    const sessionStartTime$ = this.store.select(selectSessionStartTimes)

    // Handle positions data load timing
    this.store
      .select(selectPositionsDataAvailableTimes)
      .pipe(takeUntil(this._destroyed$), withLatestFrom(sessionStartTime$))
      .subscribe(([dataAvailableTimes, sessionStartTimes]) => {
        this.processDataAvailableTimes(
          'PositionData',
          dataAvailableTimes,
          sessionStartTimes,
        )
      })

    // Handle extTx data load timing
    this.store
      .select(selectExtTxDataAvailableTimes)
      .pipe(takeUntil(this._destroyed$), withLatestFrom(sessionStartTime$))
      .subscribe(([dataAvailableTimes, sessionStartTimes]) => {
        this.processDataAvailableTimes(
          'ExtTxData',
          dataAvailableTimes,
          sessionStartTimes,
        )
      })

    // Handle cwTx data load timing
    this.store
      .select(selectCwTxDataAvailableTimes)
      .pipe(takeUntil(this._destroyed$), withLatestFrom(sessionStartTime$))
      .subscribe(([dataAvailableTimes, sessionStartTimes]) => {
        this.processDataAvailableTimes(
          'CwTxData',
          dataAvailableTimes,
          sessionStartTimes,
        )
      })
  }

  cleanup() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  processDataAvailableTimes(
    dataType: string,
    dataAvailableTimes: { [sessionId: string]: number } | undefined,
    sessionStartTimes: { [sessionId: string]: number } | undefined,
  ) {
    if (!dataAvailableTimes || !sessionStartTimes) return

    Object.entries(dataAvailableTimes).forEach(
      ([sessionId, dataAvailableTime]) => {
        const startTime = sessionStartTimes[sessionId]
        if (startTime) {
          const durationMs = dataAvailableTime - startTime
          this.telemetryService.emitCustomEvent(`helios${dataType}LoadTiming`, {
            dataType: dataType,
            sessionId: sessionId,
            loadDuration: durationMs,
          })
        }
      },
    )
  }
}
